import { useEffect, useState } from 'react';
import TitleBar from './components/ui/TitleBar';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const API_BASE = getApiBase();
const EMPTY_FORM = { name: '', note_text: '' };
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const SavedNotesSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [savedNotes, setSavedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSavedNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSavedNotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings/saved-notes`, {
        headers: { ...getTenantHeaders() }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Load failed (HTTP ${response.status})`);
      }
      setSavedNotes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load saved notes:', error);
      setSavedNotes([]);
      alert('Failed to load saved notes.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const validateForm = () => {
    const name = form.name.trim();
    const noteText = form.note_text.trim();
    if (!name || !noteText) {
      alert('TITLE AND NOTE ARE REQUIRED.');
      return null;
    }
    return { name, note_text: noteText };
  };

  const closeForm = () => {
    setShowAddNote(false);
    setEditingNoteId(null);
    resetForm();
  };

  const createSavedNote = async () => {
    const payload = validateForm();
    if (!payload) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings/saved-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Save failed (HTTP ${response.status})`);
      }
      closeForm();
      await loadSavedNotes();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'NOTE CREATED!' }
      }));
    } catch (error) {
      console.error('Failed to create saved note:', error);
      alert(error.message || 'Failed to save saved note.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (savedNote) => {
    setShowAddNote(false);
    setEditingNoteId(savedNote.id);
    setForm({
      name: savedNote.name || '',
      note_text: savedNote.note_text || ''
    });
  };

  const updateSavedNote = async () => {
    const payload = validateForm();
    if (!payload) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings/saved-notes/${editingNoteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Update failed (HTTP ${response.status})`);
      }
      closeForm();
      await loadSavedNotes();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'NOTE UPDATED!' }
      }));
    } catch (error) {
      console.error('Failed to update saved note:', error);
      alert(error.message || 'Failed to update saved note.');
    } finally {
      setSaving(false);
    }
  };

  const deleteSavedNote = async (savedNote) => {
    if (!window.confirm(`DELETE SAVED NOTE "${savedNote.name}"?`)) return;

    try {
      const response = await fetch(`${API_BASE}/api/settings/saved-notes/${savedNote.id}`, {
        method: 'DELETE',
        headers: { ...getTenantHeaders() }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || `Delete failed (HTTP ${response.status})`);
      }
      await loadSavedNotes();
    } catch (error) {
      console.error('Failed to delete saved note:', error);
      alert(error.message || 'Failed to delete saved note.');
    }
  };

  const openAddForm = () => {
    setEditingNoteId(null);
    resetForm();
    setShowAddNote(true);
  };

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: '1200px',
      padding: isPhone ? '0' : '0 12px',
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'),
      marginBottom: '18px'
    },
    note: {
      fontWeight: 900,
      fontSize: '13px',
      padding: '12px',
      border: '3px solid black',
      backgroundColor: '#ffffcc',
      marginBottom: '12px'
    },
    addBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px 20px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '12px'
    },
    card: {
      border: '4px solid black',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: 'white'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '10px',
      flexWrap: 'wrap'
    },
    cardInfo: {
      flex: 1,
      minWidth: '200px'
    },
    cardTitle: {
      fontWeight: 900,
      fontSize: '20px'
    },
    cardNote: {
      fontWeight: 700,
      fontSize: '15px',
      lineHeight: 1.5,
      marginTop: '8px',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      overflow: 'hidden'
    },
    cardActions: {
      display: 'flex',
      gap: '8px'
    },
    editBtn: {
      background: 'white',
      color: 'black',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    deleteBtn: {
      background: '#ff4444',
      color: 'white',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    },
    modal: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 5000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      border: '8px solid black',
      padding: '24px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontWeight: 900,
      fontSize: '22px',
      marginBottom: '16px',
      textTransform: 'uppercase'
    },
    row: {
      marginBottom: '12px'
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '13px',
      marginBottom: '6px',
      display: 'block'
    },
    input: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box'
    },
    textarea: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      minHeight: '180px',
      fontFamily: 'inherit',
      resize: 'vertical'
    },
    modalBtnRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '16px'
    },
    confirmBtn: {
      flex: 1,
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    cancelBtn: {
      flex: 1,
      background: 'white',
      color: 'black',
      border: '4px solid black',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    }
  };

  const formModal = (title, onSave) => (
    <div style={styles.modal} onClick={closeForm}>
      <div style={styles.modalContent} onClick={event => event.stopPropagation()}>
        <h2 style={styles.modalTitle}>{title}</h2>

        <div style={styles.row}>
          <label style={styles.label}>TITLE *</label>
          <input
            style={styles.input}
            value={form.name}
            onChange={event => setForm({ ...form, name: event.target.value })}
            maxLength={255}
            required
          />
        </div>

        <div style={styles.row}>
          <label style={styles.label}>NOTE *</label>
          <textarea
            style={styles.textarea}
            value={form.note_text}
            onChange={event => setForm({ ...form, note_text: event.target.value })}
            maxLength={10000}
            required
          />
        </div>

        <div style={styles.modalBtnRow}>
          <button className="jf-saved-notes-hover" style={styles.confirmBtn} onClick={onSave} disabled={saving}>
            {saving ? 'SAVING...' : 'SAVE'}
          </button>
          <button className="jf-saved-notes-hover" style={styles.cancelBtn} onClick={closeForm} disabled={saving}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <style>{`
        .jf-saved-notes-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="📝"
          title="SAVED NOTES"
          accent="#E8761D"
          right={
            <button
              className="jf-saved-notes-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING SAVED NOTES...</div>
          ) : (
            <>
              <div style={styles.note}>
                Create reusable notes for estimates and invoices.
              </div>

              <button className="jf-saved-notes-hover" style={styles.addBtn} onClick={openAddForm}>
                + ADD SAVED NOTE
              </button>

              {savedNotes.length === 0 ? (
                <div style={{ ...styles.note, backgroundColor: 'white' }}>
                  No saved notes added yet.
                </div>
              ) : (
                savedNotes.map(savedNote => (
                  <div key={savedNote.id} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardTitle}>{savedNote.name}</div>
                        <div style={styles.cardNote}>{savedNote.note_text}</div>
                      </div>
                      <div style={styles.cardActions}>
                        <button className="jf-saved-notes-hover" style={styles.editBtn} onClick={() => startEdit(savedNote)}>
                          EDIT
                        </button>
                        <button className="jf-saved-notes-hover" style={styles.deleteBtn} onClick={() => deleteSavedNote(savedNote)}>
                          DELETE
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>

        {showAddNote && formModal('ADD SAVED NOTE', createSavedNote)}
        {editingNoteId && formModal('EDIT SAVED NOTE', updateSavedNote)}
      </div>
    </div>
  );
};

export default SavedNotesSettings;
